import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { memo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { CenterFlex, Divider, Flex, Space, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import TransitionPortal from '@/components/Common/TransitionPortal';
import Heading from '@/components/Typography/Heading';
import { SpanStyle } from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import useColor from '@/hooks/domain/useColor';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useFetch from '@/hooks/pure/useFetch';
import useKeymap from '@/hooks/pure/useKeymap';
import useGlobalSearchStore from '@/stores/GlobalSearchStore';
import { radius } from '@/styles/sizes';
import { hiddenScrollbar } from '@/styles/style';
import { track } from '@/utils/track/track';

import useGlobalSearchModalAction from '../hooks/useGlobalSearchModalAction';
import SearchDetail from './GlobalSearchDetail/SearchDetail';
import SearchHistoryList from './SearchHistoryList';
import SearchQueryBuilder from './SearchQueryBuilder/SearchQueryBuilder';

const MODAL_MARGIN = 32;

const GlobalSearchModal = () => {
  const color = useColor();
  const status = useGlobalSearchStore((state) => state.status);
  const { closeModal } = useGlobalSearchModalAction();
  return (
    <TransitionPortal
      layerId="content-modal-layer"
      status={status}
      duration={200}
      style={{
        top: MODAL_MARGIN,
        left: 'calc(50% - 700px)',
        width: 1400,
        height: 'calc(100vh - 64px)',
        borderRadius: 20,
        backgroundColor: color.gray100,
        boxShadow: 'none',
        overflow: 'auto',
      }}
      close={closeModal}
      background={{
        enable: true,
        style: {
          backgroundColor: color.FD40,
          backdropFilter: 'blur(12px)',
        },
      }}
      animations={{
        in: modalInAnim,
        out: modalOutAnim,
      }}
    >
      <Content />
    </TransitionPortal>
  );
};

const Content = () => {
  const alias = useGlobalSearchStore((state) => state.alias);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const syncSearchView = useGlobalSearchStore((state) => state.syncSearchView);

  const { closeModal, refreshHistoryList, setSearchStoreState } =
    useGlobalSearchModalAction();

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useFetch(syncSearchView);
  useKeymap('Escape', closeModal);

  const onNewSearchClick = () => {
    track(AMP_KEYS.SEARCH_NEW_SEARCH_CLICK);
    setSearchStoreState({
      alias: null,
    });
  };

  useTrackModal({
    domain: 'global-search',
    alias: alias || '',
  });

  return (
    <Transition
      css={css`
        flex: 1;
        height: 100%;
        overflow: auto;
        display: flex;
        flex-direction: column;
      `}
      status={true}
    >
      <Style ref={modalContentRef}>
        <VFlex className="history-panel" g={16}>
          <Flex className="history-navigation">
            <SearchNavigationItem
              selected={alias === null}
              onClick={onNewSearchClick}
            >
              <SearchOutlined />
              New search
            </SearchNavigationItem>
          </Flex>
          <SearchHistoryList />
        </VFlex>
        <Divider direction="v" />
        <VFlex className="detail-panel">
          <Transition
            css={css`
              display: flex;
              flex-direction: column;
              flex: 1;
              height: calc(100% - 106px);
              overflow: hidden;
            `}
            status={alias ? true : false}
            fallback={
              <VFlex f={1}>
                <IconButton
                  icon={<CloseOutlined />}
                  onClick={closeModal}
                  sizeType="m"
                  style={{
                    position: 'absolute',
                    top: 32,
                    right: 32,
                    zIndex: 10,
                  }}
                />

                <CenterFlex f={1} d="column" j="center" g={8}>
                  <VFlex g={16} j="center" a="center">
                    <Heading type="h1" weight="sb">
                      Customer insights in seconds
                    </Heading>
                  </VFlex>
                  <Space h={20} />
                  <Flex
                    style={{ width: 700, position: 'relative', height: 60 }}
                  >
                    <Flex
                      style={{
                        position: 'absolute',
                        width: 700,
                        zIndex: 10,
                        transform: isSearchOpen
                          ? 'translateY(-160px)'
                          : 'translateY(0)',
                        transition: 'transform 0.3s ease-in-out',
                      }}
                    >
                      <SearchQueryBuilder
                        autoFocus
                        from="modal"
                        onOpenStateChange={setIsSearchOpen}
                        onSubmitCallback={refreshHistoryList}
                      />
                    </Flex>
                  </Flex>

                  <Space h={160} />
                </CenterFlex>
              </VFlex>
            }
          >
            {alias && <SearchDetail alias={alias} />}
          </Transition>
        </VFlex>
      </Style>
    </Transition>
  );
};

const Style = styled(Flex)`
  overflow: auto;
  flex: 1;
  .history-panel {
    overflow: auto;
    width: 300px;
    padding: 24px 0px 0px 20px;
    .history-navigation {
      padding-right: 26px;
    }
  }
  .detail-panel {
    position: relative;
    flex: 1;
    overflow: auto;
    scrollbar-gutter: stable;
    ${hiddenScrollbar};
  }
`;

const modalInAnim = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  30% {
    opacity: 0;
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const modalOutAnim = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  30% {
    opacity: 0;
  }
  100% {
    transform: scale(0.8);
    opacity: 0;
  }
`;

const SearchNavigationItem = styled(Flex)<{ selected: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: ${radius.label}px;
  ${SpanStyle({
    type: 'label1',
    weight: 'm',
  })};
  color: ${(props) => props.theme.colors.gray800};
  gap: 12px;
  transition: all 0.2s;
  border: 1px solid transparent;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
  }
  ${(props) =>
    props.selected &&
    css`
      background-color: ${(props) => props.theme.colors.gray200};
      border-color: ${(props) => props.theme.colors.primary100};
    `}
`;

export default memo(GlobalSearchModal);
